/**
 * Child half of the in-app embed protocol.
 *
 * The cancel experience is the same document hosted or framed. Only one thing
 * differs, and it is a transport concern rather than experience configuration:
 * a top-level navigation has to be performed by the merchant page, because a
 * cross-origin iframe cannot navigate its parent. Keeping that out of the
 * card/action vocabulary is deliberate — a `link` action with a `url` means the
 * same thing in both modes, and only the executor changes.
 */

export const EMBED_MESSAGE_PREFIX = 'ChargebeeRetention:cancel';

/** Present on the iframe URL to tell this document it is embedded. */
export const EMBED_QUERY_FLAG = 'cbr_embed';

/** Merchant origin, so messages can be targeted rather than broadcast. */
export const EMBED_ORIGIN_QUERY = 'cbr_embed_origin';

export type EmbedReadyMessage = {
  type: `${typeof EMBED_MESSAGE_PREFIX}:ready`;
};

export type EmbedNavigateMessage = {
  type: `${typeof EMBED_MESSAGE_PREFIX}:navigate`;
  url: string;
};

export type EmbedMessage = EmbedReadyMessage | EmbedNavigateMessage;

/**
 * A union rather than a flag beside an origin, so "embedded with nowhere to
 * post" cannot be expressed. Embed mode always carries a real target.
 */
export type EmbedParams =
  { isEmbedded: false } | { isEmbedded: true; parentOrigin: string };

export type EmbedBridge = {
  /**
   * Signals that the experience has painted. The surface keeps the overlay
   * hidden until this arrives, and treats its absence as a framing failure.
   */
  ready: () => void;
  navigate: (url: string) => void;
};

const NOT_EMBEDDED = { isEmbedded: false } as const;

/**
 * The origin to post back to, or null when the SDK gave us nothing usable.
 * There is deliberately no wildcard fallback: this document's messages carry
 * the session's redirect destinations, and broadcasting would hand them to
 * whatever framed it.
 */
const parseParentOrigin = (raw: string | null): string | null => {
  if (!raw) {
    return null;
  }
  try {
    const { origin } = new URL(raw);
    return origin && origin !== 'null' ? origin : null;
  } catch {
    return null;
  }
};

export const readEmbedParams = (
  search: string = window.location.search
): EmbedParams => {
  const params = new URLSearchParams(search);
  if (params.get(EMBED_QUERY_FLAG) !== '1') {
    return NOT_EMBEDDED;
  }

  // The flag rides on the URL, so it survives being copied out of the frame.
  // Opened as a top-level document it would post navigations to itself with
  // nothing listening, leaving the experience's own exits looking dead, so
  // having a parent is checked rather than inferred from the flag.
  const parentOrigin = parseParentOrigin(params.get(EMBED_ORIGIN_QUERY));
  if (!parentOrigin || window.parent === window) {
    return NOT_EMBEDDED;
  }

  return { isEmbedded: true, parentOrigin };
};

/** This document's URL as it would have been without embedding. */
export const hostedUrl = (href: string = window.location.href): string => {
  const url = new URL(href);
  url.searchParams.delete(EMBED_QUERY_FLAG);
  url.searchParams.delete(EMBED_ORIGIN_QUERY);
  return url.toString();
};

/**
 * Confirmation destinations arrive root-relative — `GlobalUrlService` builds
 * `/{company}/cancel/complete/...` and its validator requires the leading
 * slash. Hosted, the browser resolves that against the experience origin. The
 * merchant window would resolve it against its own, so it has to be absolute
 * before it leaves this document, which is the only side that knows the base.
 */
const absolutize = (url: string): string => {
  try {
    return new URL(url, window.location.href).toString();
  } catch {
    return url;
  }
};

export const createEmbedBridge = (parentOrigin: string): EmbedBridge => {
  const post = (message: EmbedMessage) =>
    window.parent?.postMessage(message, parentOrigin);

  return {
    ready: () => post({ type: `${EMBED_MESSAGE_PREFIX}:ready` }),
    navigate: (url) =>
      post({ type: `${EMBED_MESSAGE_PREFIX}:navigate`, url: absolutize(url) }),
  };
};
